/**
 * The wall: a little guy on the floor, and the room scrolling to keep up with him.
 *
 * The page is complete without this file -- the room is an ordinary horizontal scroll
 * container and every poster is a <button> -- so what's added here is:
 *
 *   - the guy. Arrow keys (or A/D) walk him along the track; the room scrolls to keep him
 *     around 45% of the way across the screen, until it runs out of room and he walks the
 *     rest of the way to the edge himself.
 *   - the other direction: when the room is scrolled by hand (trackpad, finger, wheel),
 *     the guy walks to catch up with the viewport, so he is always the thing that moves.
 *   - the poster he's standing in front of gets .poster--near, and Up/Enter/Space opens it.
 *   - the lightbox: the lossless scan, big, with its index card; arrows step along the
 *     wall (and walk the guy there behind the dialog).
 *
 * One requestAnimationFrame loop owns the guy's position. Keys set a direction, scroll
 * sets a target, the loop resolves both and paints once per frame.
 */

const room = document.querySelector('.room');
const track = document.querySelector('.track');
const guy = document.querySelector('.guy');
const posters = Array.from(document.querySelectorAll('.poster'));
const dialog = document.querySelector('.lightbox');

if (room && track && guy) {
  init();
}

function init() {
  const WALK = 520; // px per second, on foot
  const CATCH_UP = 6; // how eagerly he chases a hand-scrolled viewport (per second)
  const ANCHOR = 0.45; // where on screen he stands while the room scrolls

  const held = new Set(); // 'left' | 'right'
  let x = 0; // guy's world x (centre), in track pixels
  let dir = 1;
  let walkTo = null; // a world x he's heading for on his own (clicked poster), or null
  let onArrive = null;
  let chase = null; // where a hand-scrolled viewport wants him, or null
  let lastScroll = room.scrollLeft;
  let lastTime = performance.now();
  let near = null;

  // Poster centres in track pixels; recomputed when the layout changes.
  let centres = [];
  const measure = () => {
    centres = posters.map(p => {
      const rect = p.getBoundingClientRect();
      const trackRect = track.getBoundingClientRect();
      return {
        el: p,
        x: rect.left - trackRect.left + rect.width / 2,
        half: rect.width / 2,
      };
    });
  };
  measure();
  window.addEventListener('resize', measure);
  // Images finish loading after first paint and can widen their figures; re-measure once
  // they have all settled rather than on every single load.
  window.addEventListener('load', measure);

  const halfGuy = () => guy.offsetWidth / 2;
  const clampX = value =>
    Math.max(halfGuy() + 8, Math.min(track.scrollWidth - halfGuy() - 8, value));
  const maxScroll = () => Math.max(0, track.scrollWidth - room.clientWidth);

  // Start him a little way in, in front of the first thing -- or, if the URL names a
  // poster (#wall2-03, the figure's id), straight in front of that one.
  const snapTo = el => {
    const target = centres.find(c => c.el === el);
    if (!target) return;
    x = clampX(target.x);
    room.scrollLeft = Math.max(0, Math.min(maxScroll(), x - room.clientWidth * ANCHOR));
    lastScroll = room.scrollLeft;
  };
  x = clampX(room.clientWidth * ANCHOR);
  const linked = location.hash ? document.getElementById(location.hash.slice(1)) : null;
  if (linked?.classList.contains('poster')) snapTo(linked);

  // The wall signs are set in a web font, so everything after the first one shifts a
  // little when it lands. Measure again then, and if nobody has moved yet, land the deep
  // link a second time on the settled layout.
  let touched = false;
  document.fonts?.ready.then(() => {
    measure();
    if (linked?.classList.contains('poster') && !touched) snapTo(linked);
  });

  // ------------------------------------------------------------------ Keys

  const keyDir = event => {
    switch (event.key) {
      case 'ArrowLeft':
      case 'a':
      case 'A':
        return 'left';
      case 'ArrowRight':
      case 'd':
      case 'D':
        return 'right';
      default:
        return null;
    }
  };

  window.addEventListener('keydown', event => {
    if (dialog?.open) return; // the dialog has its own keys, below
    if (event.metaKey || event.ctrlKey || event.altKey) return;

    const d = keyDir(event);
    if (d) {
      event.preventDefault();
      held.add(d);
      touched = true;
      walkTo = null;
      chase = null;
      return;
    }

    if (
      event.key === 'ArrowUp' ||
      event.key === 'Enter' ||
      event.key === ' ' ||
      event.key === 'w' ||
      event.key === 'W'
    ) {
      // Enter/Space on a focused poster button is a click already; leave it to that.
      if (
        event.target instanceof HTMLElement &&
        event.target.closest('.poster__btn') &&
        event.key !== 'ArrowUp'
      )
        return;
      if (near) {
        event.preventDefault();
        open(near.el);
      }
    }
  });

  window.addEventListener('keyup', event => {
    const d = keyDir(event);
    if (d) held.delete(d);
  });

  window.addEventListener('blur', () => held.clear());

  // A vertical wheel on a sideways room means sideways. Trackpads already send deltaX.
  room.addEventListener(
    'wheel',
    event => {
      if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;
      event.preventDefault();
      room.scrollLeft += event.deltaY;
    },
    { passive: false },
  );

  // ----------------------------------------------------------------- Click

  track.addEventListener('click', event => {
    const btn = event.target.closest('.poster__btn');
    if (!btn) return;
    touched = true;
    const poster = btn.closest('.poster');
    const target = centres.find(c => c.el === poster);
    if (!target) return open(poster);

    // Walk over first, then look. If he's already there (or nearly), just look.
    if (Math.abs(target.x - x) < 40) return open(poster);
    walkTo = clampX(target.x);
    chase = null;
    onArrive = () => open(poster);
  });

  // ------------------------------------------------------------------ Loop

  const frame = now => {
    const dt = Math.min(0.05, (now - lastTime) / 1000);
    lastTime = now;

    let walking = false;
    const scrolledByHand = room.scrollLeft !== lastScroll;
    if (scrolledByHand) touched = true;

    if (held.size) {
      // On foot: he moves, the room follows.
      const d =
        held.has('right') && !held.has('left')
          ? 1
          : held.has('left') && !held.has('right')
            ? -1
            : 0;
      if (d) {
        dir = d;
        x = clampX(x + d * WALK * dt);
        walking = true;
      }
      room.scrollLeft = Math.max(0, Math.min(maxScroll(), x - room.clientWidth * ANCHOR));
    } else if (walkTo !== null) {
      // Heading somewhere on his own.
      const delta = walkTo - x;
      if (Math.abs(delta) <= WALK * dt) {
        x = walkTo;
        walkTo = null;
        const done = onArrive;
        onArrive = null;
        done?.();
      } else {
        dir = Math.sign(delta);
        x += dir * WALK * dt;
        walking = true;
      }
      room.scrollLeft = Math.max(0, Math.min(maxScroll(), x - room.clientWidth * ANCHOR));
    } else {
      // The room moved without him (finger, trackpad, wheel, a focused poster scrolling
      // itself into view): note where he should be standing now, and walk there over the
      // next few frames rather than teleporting.
      if (scrolledByHand) chase = clampX(room.scrollLeft + room.clientWidth * ANCHOR);
      if (chase !== null) {
        const delta = chase - x;
        if (Math.abs(delta) <= 1) {
          x = chase;
          chase = null;
        } else {
          dir = Math.sign(delta);
          const step = delta * Math.min(1, CATCH_UP * dt);
          x +=
            Math.abs(step) < WALK * dt * 0.5
              ? Math.sign(delta) * Math.min(Math.abs(delta), WALK * dt * 0.5)
              : step;
          walking = Math.abs(delta) > 6;
        }
      }
    }

    lastScroll = room.scrollLeft;

    guy.style.setProperty('--x', `${x - halfGuy()}px`);
    guy.style.setProperty('--dir', String(dir));
    guy.classList.toggle('guy--walking', walking);

    updateNear();
    requestAnimationFrame(frame);
  };

  function updateNear() {
    let best = null;
    let bestDist = Infinity;
    for (const c of centres) {
      const dist = Math.abs(c.x - x);
      if (dist < bestDist) {
        bestDist = dist;
        best = c;
      }
    }
    // "In front of" means within the poster's own width, give or take a stride.
    if (best && bestDist > best.half + 48) best = null;
    if (best === near) return;
    near?.el.classList.remove('poster--near');
    near = best;
    near?.el.classList.add('poster--near');
  }

  requestAnimationFrame(frame);

  // ----------------------------------------------------------------- Idle
  // Standing still is one frame of the walking gif, drawn to a canvas the way the game
  // does it; the stylesheet shows the canvas when he's not walking once .guy--ready is on.
  const walk = guy.querySelector('.guy__walk');
  const idle = guy.querySelector('.guy__idle');
  const drawIdle = () => {
    try {
      idle.width = walk.naturalWidth || 186;
      idle.height = walk.naturalHeight || 200;
      idle.getContext('2d').drawImage(walk, 0, 0, idle.width, idle.height);
      guy.classList.add('guy--ready');
    } catch {
      /* the gif keeps playing; still a penguin */
    }
  };
  if (walk && idle) {
    if (walk.complete && walk.naturalWidth) drawIdle();
    else walk.addEventListener('load', drawIdle, { once: true });
  }

  // -------------------------------------------------------------- Lightbox

  function open(poster) {
    if (!dialog) return;
    const img = dialog.querySelector('.lightbox__img');
    const card = dialog.querySelector('.lightbox__card');
    const thumb = poster.querySelector('.poster__img');
    const caption = poster.querySelector('.card');

    // Thumb first, so something is on screen at once; the lossless scan replaces it when
    // it lands. Both are the same picture, so nothing jumps but the sharpness.
    img.src = thumb.currentSrc || thumb.src;
    img.alt = thumb.alt;
    img.width = thumb.naturalWidth || Number(thumb.getAttribute('width'));
    img.height = thumb.naturalHeight || Number(thumb.getAttribute('height'));
    const full = poster.dataset.full;
    if (full) {
      const loader = new Image();
      loader.onload = () => {
        if (dialog.dataset.showing === poster.dataset.index) img.src = full;
      };
      loader.src = full;
    }

    if (caption) {
      card.innerHTML = caption.innerHTML;
      card.hidden = false;
    } else {
      card.innerHTML = '';
      card.hidden = true;
    }

    dialog.dataset.showing = poster.dataset.index;
    if (poster.id) history.replaceState(null, '', `#${poster.id}`);
    const i = posters.indexOf(poster);
    dialog.querySelector('.lightbox__step--prev').disabled = i <= 0;
    dialog.querySelector('.lightbox__step--next').disabled = i >= posters.length - 1;

    if (!dialog.open) dialog.showModal();
  }

  function step(delta) {
    const current = posters.find(p => p.dataset.index === dialog.dataset.showing);
    const i = posters.indexOf(current);
    const next = posters[i + delta];
    if (!next) return;
    open(next);
    // Walk him there behind the dialog so he's standing in the right place when it closes.
    const target = centres.find(c => c.el === next);
    if (target) {
      walkTo = clampX(target.x);
      chase = null;
      onArrive = null;
    }
  }

  if (dialog) {
    dialog.querySelector('.lightbox__step--prev').addEventListener('click', () => step(-1));
    dialog.querySelector('.lightbox__step--next').addEventListener('click', () => step(1));
    dialog.querySelector('.lightbox__close').addEventListener('click', () => dialog.close());

    dialog.addEventListener('keydown', event => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        step(-1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        step(1);
      } else if (event.key === 'ArrowDown') {
        event.preventDefault();
        dialog.close();
      }
    });

    // Click the dark to dismiss: anything that isn't the figure or a button is the dialog.
    dialog.addEventListener('click', event => {
      if (event.target === dialog) dialog.close();
    });

    dialog.addEventListener('close', () => {
      delete dialog.dataset.showing;
      if (location.hash) history.replaceState(null, '', location.pathname);
      held.clear();
      room.focus({ preventScroll: true });
    });
  }
}
